import { type CSSProperties, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { CustomerApiError, customerApi } from '../customer_api';
import { type BrandResponse, FALLBACK_PRIMARY } from '../lib/brand';
import { CustomerBrandContext } from '../lib/brandContext';
import { SpaceNounContext } from '../lib/spaceNounContext';

interface BrandedShellState {
  brand: BrandResponse | null;
  loaded: boolean;
}

/**
 * Applies the tenant's primary colour to its subtree via `--brand-primary` +
 * `[data-branded] { --primary }`.
 *
 * Branding comes from the root `CustomerBrandProvider` when one is mounted
 * (the normal app path — one fetch per page load). When no provider is
 * present (a page rendered standalone in a test, or the chrome-less embed)
 * the shell fetches `GET /api/customer/branding` itself, as it always did.
 */
export function BrandedShell({ children }: { children: ReactNode }) {
  const root = useContext(CustomerBrandContext);
  const [local, setLocal] = useState<BrandedShellState>({ brand: null, loaded: false });

  useEffect(() => {
    if (root) return; // root provider owns the fetch
    customerApi<BrandResponse>('/api/customer/branding')
      .then((b) => setLocal({ brand: b, loaded: true }))
      .catch((err) => {
        // Tenant not resolved (404) or other — render with platform defaults.
        console.warn('Failed to load brand:', err instanceof CustomerApiError ? err.status : err);
        setLocal({ brand: null, loaded: true });
      });
  }, [root]);

  const brand = root ? root.brand : local.brand;
  const primary = brand?.brand.primaryColour ?? FALLBACK_PRIMARY;
  const style: CSSProperties = { ['--brand-primary' as string]: primary };
  const spaceNoun = brand?.spaceNoun ?? null;

  return (
    <SpaceNounContext.Provider value={spaceNoun}>
      <div style={style}>
        {/* Override --primary for descendants. Inline style scoped via attribute. */}
        <style>{`[data-branded] { --primary: ${primary}; }`}</style>
        <div data-branded>{children}</div>
      </div>
    </SpaceNounContext.Provider>
  );
}
